import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useEffect, useMemo, useState } from "react";
import { Info, Search } from "lucide-react";
import { useRestrictedWords, useMattersCache } from "@/shared/cache";
import type { RestrictedWord, Matter } from "@/shared/cache";
import type { SearchableField } from "@/shared/utils/smartSearch";
import { ComboBox } from "@/v2/components/ui/combobox";
import { Input } from "@/v2/components/ui/input";
import {
	renderMatterItem,
	renderSelectedMatter,
} from "@/v2/components/entry-form/pickerItems";

// ── Helpers ────────────────────────────────────────────────────────

function RuleRow({ rule }: { rule: RestrictedWord }) {
	return (
		<div className="flex gap-3 px-4 py-3 border-b border-border last:border-b-0">
			<span
				className={`mt-1.5 size-2 shrink-0 rounded-full ${
					rule.severity === "block" ? "bg-red-500" : "bg-amber-500"
				}`}
			/>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium">{rule.displayName}</p>
				<p className="text-xs text-muted-foreground">
					{rule.suggestion ? (
						<>
							Suggestion:{" "}
							<span className="font-medium text-foreground">
								{rule.suggestion}
							</span>
						</>
					) : (
						"No replacement suggested"
					)}
				</p>
			</div>
			<span className="shrink-0 text-xs text-muted-foreground mt-0.5">
				{rule.severity === "block" ? "Block" : "Warning"}
			</span>
		</div>
	);
}

function RuleSection({
	title,
	rules,
	variant,
}: {
	title: string;
	rules: RestrictedWord[];
	variant: "blocked" | "warning";
}) {
	if (rules.length === 0) return null;

	return (
		<div>
			<h3
				className={`text-xs font-medium uppercase tracking-wide mb-1.5 ${
					variant === "blocked"
						? "text-red-600 dark:text-red-400"
						: "text-amber-600 dark:text-amber-400"
				}`}
			>
				{title} ({rules.length})
			</h3>
			<div className="rounded-lg border border-border overflow-hidden">
				{rules.map((rule, index) => (
					<RuleRow key={`${rule.word}-${index}`} rule={rule} />
				))}
			</div>
		</div>
	);
}

// ── Component ──────────────────────────────────────────────────────

export default function RestrictedWords() {
	useDocumentTitle("Restricted Words");
	const { getRestrictedWords, fetchAllForClient, isLoading: cacheLoading } = useRestrictedWords();
	const firmWideWords = getRestrictedWords();
	const { matters } = useMattersCache();

	const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
	const [scopedRules, setScopedRules] = useState<RestrictedWord[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [wordSearch, setWordSearch] = useState("");

	// Use scoped rules if a matter is selected, otherwise firm-wide
	const rules = selectedMatterId ? (scopedRules ?? []) : firmWideWords;

	const selectedMatter = useMemo(
		() => matters.find((m) => m.matter_id === selectedMatterId) || null,
		[matters, selectedMatterId],
	);

	// Fetch rules when matter selection changes
	useEffect(() => {
		if (selectedMatterId) {
			const clientCode = selectedMatterId.split(".")[0];
			const matterCode = selectedMatterId.split(".").slice(1).join(".");
			let cancelled = false;
			setIsLoading(true);
			setScopedRules(null);
			fetchAllForClient(clientCode, matterCode)
				.then((fetched) => {
					if (!cancelled) setScopedRules(fetched);
				})
				.catch(() => {
					if (!cancelled) setScopedRules([]);
				})
				.finally(() => {
					if (!cancelled) setIsLoading(false);
				});
			return () => {
				cancelled = true;
			};
		} else {
			setScopedRules(null);
			setIsLoading(false);
		}
	}, [selectedMatterId, fetchAllForClient]);

	// Filter rules by word search
	const filteredRules = useMemo(() => {
		const query = wordSearch.trim().toLowerCase();
		if (!query) return rules;
		return rules.filter(
			(r) =>
				r.displayName.toLowerCase().includes(query) ||
				(r.suggestion?.toLowerCase().includes(query) ?? false),
		);
	}, [rules, wordSearch]);

	const blocked = useMemo(
		() => filteredRules.filter((r) => r.severity === "block"),
		[filteredRules],
	);
	const warnings = useMemo(
		() => filteredRules.filter((r) => r.severity === "warning"),
		[filteredRules],
	);

	// ComboBox field extractors
	const matterFields: ((item: Matter) => SearchableField)[] = useMemo(
		() => [
			(m: Matter) => ({ value: m.matter, weight: 1.5 }),
			(m: Matter) => ({ value: m.client, weight: 1.2 }),
			(m: Matter) => ({ value: m.description, weight: 1.0 }),
		],
		[],
	);

	const loading = cacheLoading || isLoading;

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">Restricted Words</h2>

			<div className="flex flex-col gap-3">
				{/* Matter / Client filter */}
				<div>
					<label className="text-xs font-medium text-muted-foreground mb-1 block">
						Client / Matter
					</label>
					<ComboBox
						items={matters}
						value={selectedMatterId}
						onChange={(id) => setSelectedMatterId(id || null)}
						fields={matterFields}
						getKey={(m) => m.matter_id}
						getDisplayLabel={(m) =>
							`${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
						}
						getSearchValue={(m) => `${m.matter.trim()} ${m.description.trim()}`}
						renderItem={renderMatterItem}
						renderSelected={renderSelectedMatter}
						placeholder="All clients (firm-wide)"
						clearable
						onClear={() => setSelectedMatterId(null)}
					/>
				</div>

				{/* Word search */}
				<div>
					<label className="text-xs font-medium text-muted-foreground mb-1 block">
						Search words
					</label>
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
						<Input
							value={wordSearch}
							onChange={(e) => setWordSearch(e.target.value)}
							placeholder="Search restricted words..."
							className="pl-8 h-9 text-sm"
						/>
					</div>
				</div>

				{/* Info banner */}
				{(selectedMatter || wordSearch) && (
					<div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
						<Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
						<span className="text-xs text-muted-foreground">
							{selectedMatter && (
								<>
									Showing rules for{" "}
									<span className="font-medium text-foreground">
										{selectedMatter.client}
									</span>{" "}
									— {selectedMatter.description} (includes firm-wide rules).
								</>
							)}
							{wordSearch && (
								<>
									{selectedMatter && " "}Filtered to {filteredRules.length} of{" "}
									{rules.length} rules.
								</>
							)}
						</span>
					</div>
				)}

				{/* Rules content */}
				{loading ? (
					<div className="space-y-3">
						<div className="h-10 rounded-lg bg-muted animate-pulse" />
						<div className="h-10 rounded-lg bg-muted animate-pulse" />
						<div className="h-10 rounded-lg bg-muted animate-pulse" />
					</div>
				) : rules.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
						<p className="text-sm text-muted-foreground">
							No restricted words configured.
						</p>
					</div>
				) : filteredRules.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center">
						<p className="text-sm text-muted-foreground">
							No restricted words match &ldquo;{wordSearch}&rdquo;
						</p>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						<RuleSection title="Blocked" rules={blocked} variant="blocked" />
						<RuleSection title="Warnings" rules={warnings} variant="warning" />
					</div>
				)}
			</div>
		</div>
	);
}
